/**
 * File operations — OpenAI-compatible Files API (list, get, delete, upload, content).
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData } from '../errors.js'
import { authenticatedFetch, type AuthHeaders } from '../fetch.js'

type FileObject = components['schemas']['FileObject']
type FileDeleteResponse = components['schemas']['FileDeleteResponse']
type FileListResponse = components['schemas']['FileListResponse']

export type { FileObject, FileDeleteResponse, FileListResponse }

export interface ListFilesOptions {
  purpose?: string | null
  limit?: number
  order?: string
  after?: string | null
}

export async function listFiles(arbi: ArbiClient, options?: ListFilesOptions) {
  return requireData(
    await arbi.fetch.GET('/v1/files', {
      params: { query: options ?? {} },
    }),
    'Failed to list files'
  )
}

export async function getFile(arbi: ArbiClient, fileId: string) {
  return requireData(
    await arbi.fetch.GET('/v1/files/{file_id}', {
      params: { path: { file_id: fileId } },
    }),
    'Failed to get file'
  )
}

export async function deleteFile(arbi: ArbiClient, fileId: string) {
  return requireData(
    await arbi.fetch.DELETE('/v1/files/{file_id}', {
      params: { path: { file_id: fileId } },
    }),
    'Failed to delete file'
  )
}

/**
 * Upload a file via the OpenAI-compatible Files API.
 * Uses raw fetch for multipart upload.
 */
export async function uploadFile(
  auth: AuthHeaders,
  fileData: Blob,
  fileName: string,
  purpose = 'assistants'
): Promise<FileObject> {
  const formData = new FormData()
  formData.append('file', fileData, fileName)
  formData.append('purpose', purpose)

  const res = await authenticatedFetch({
    ...auth,
    path: '/v1/files',
    method: 'POST',
    body: formData,
  })

  return res.json() as Promise<FileObject>
}

/**
 * Get file content (binary). Returns the raw Response for the caller to handle.
 */
export async function getFileContent(auth: AuthHeaders, fileId: string): Promise<Response> {
  return authenticatedFetch({
    ...auth,
    path: `/v1/files/${fileId}/content`,
  })
}
